import { Koi } from '@ksyos/koi';
import type { ServiceOrganizationSettingType } from '@common/interfaces';
import type {
    IAddCarePathServiceConsumerSettingsValidated,
    IServiceConsumerSettingsValidated,
} from './addAdvancedCarePathSettings/addServiceConsumerSettings';
import type {
    IAddCarePathServiceEmploymentSettingsValidated,
    IServiceEmploymentActivitiesSettingsValidated,
} from './addAdvancedCarePathSettings/addServiceEmploymentSettings';
import type {
    IAddCarePathServiceProviderSettingsValidated,
    IServiceProviderSettingsValidated,
} from './addAdvancedCarePathSettings/addServiceProviderSettings';
import type { IAddCarePathServiceReaderSettingsValidated } from './addAdvancedCarePathSettings/addServiceReaderSettings';
import type {
    IAddCarePathServiceSupervisorSettingsValidated,
    IServiceSupervisorSettingsValidated,
} from './addAdvancedCarePathSettings/addServiceSupervisorSettings';
import type { ISchemaContainer } from './schemaContainer';
import { EpisodeCategory } from '../enums';
import { BittiumRecordingAnalyst } from '../enums/bittiumRecordingAnalyst';
import { ServiceCode } from '../enums/serviceCode';
import { ServiceType } from '../enums/serviceType';
import { SomnologyOrganizationType } from '../enums/somnologyOrganizationType';
import {
    addCarePathServiceConsumerSettingsAdd,
    serviceConsumerSettingsAdd,
} from './addAdvancedCarePathSettings/addServiceConsumerSettings';
import {
    serviceEmploymentActivitiesSettingsAdd,
    serviceEmploymentSettingsAdd,
} from './addAdvancedCarePathSettings/addServiceEmploymentSettings';
import {
    addCarePathServiceProviderSettingsAdd,
    serviceProviderSettingsAdd,
} from './addAdvancedCarePathSettings/addServiceProviderSettings';
import { serviceReaderSettingsAdd } from './addAdvancedCarePathSettings/addServiceReaderSettings';
import {
    addCarePathServiceSupervisorSettingsAdd,
    serviceSupervisorSettingsAdd,
} from './addAdvancedCarePathSettings/addServiceSupervisorSettings';
import { zorgMailNummerSchema } from './schemas/zorgMailNummerSchema';
import { serviceOrganizationSettingEmailNotificationAdd } from './serviceOrganizationNotificationSettings';
import { settingNames } from './serviceOrganizationSetting';

export interface IServiceValidated {
    code: ServiceCode;
    id: string;
    serviceType: ServiceType;
}

export interface IServiceOrganizationSettingsValidated {
    applicationWaitingTime: number | null;
    canPerformGlaucoomExamination: boolean | null;
    canReferPatientWithoutSmartphone: boolean;
    emailAddressOrganizationInbox: string | null;
    emailAddressOrganizationInboxAlt: string | null;
    embloomQuestionnaires?: string[];
    endDate: string | null;
    isRequestRecipient: boolean;
    ksyosHolterAnalystOrganizationId: string | null;
    optoConnectPracticeId: string | null;
    receiveZorgmailOnConsultOpen: boolean;
    recipientIsOwnOrganization: boolean;
    sendBittiumRecordingsTo: BittiumRecordingAnalyst | null;
    sendNotificationOrganizationEmail: boolean;
    sendNotificationOrganizationInbox: boolean;
    service: IServiceValidated;
    serviceGraderPoolId: string | null;
    serviceGroupSettingId: string | null;
    settingLabels?: ServiceOrganizationSettingType[];
    somnologyOrganizationType: SomnologyOrganizationType | null;
    startDate: string;
    treatmentWaitingTime: number | null;
    zorgmailNummer: string | null;
}

export interface IOrganizationAddCarePathServiceOrganizationSettingsValidated
    extends IServiceOrganizationSettingsValidated {
    preferredServiceOrganizationConsumers?: string[];
}

export interface IServiceGroupValidated {
    services: IServiceValidated[];
}

export interface IAddCarePathServiceOrganizationSettingsValidated {
    serviceGroups: IServiceGroupValidated[];
}

const serviceSchema = Koi.object().keys({
    code: Koi.koi().enum(ServiceCode).required(),
    id: Koi.string().uuid(),
    serviceType: Koi.koi().enum(ServiceType).required(),
});

export const serviceOrganizationSettingsSchema = serviceOrganizationSettingEmailNotificationAdd.keys({
    applicationWaitingTime: Koi.when('service.serviceType', {
        is: ServiceType.referral,
        otherwise: Koi.valid(null),
        then: Koi.number().min(0).max(300).allow(null),
    }),
    canPerformGlaucoomExamination: Koi.boolean().allow(null).optional(),
    canReferPatientWithoutSmartphone: Koi.when('service.code', {
        is: Koi.valid(ServiceCode.OSAS),
        otherwise: Koi.valid(false),
        then: Koi.boolean(),
    }),
    embloomQuestionnaires: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.forbidden(),
        then: Koi.when('service.code', {
            is: ServiceCode.GGZOembl,
            otherwise: Koi.forbidden(),
            then: Koi.array().items(Koi.string().uuid()).optional(),
        }),
    }),
    endDate: Koi.koi().date().allow(null),
    isRequestRecipient: Koi.boolean(),
    ksyosHolterAnalystOrganizationId: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.valid(null),
        then: Koi.when('service.code', {
            is: ServiceCode.TCOH,
            otherwise: Koi.valid(null),
            then: Koi.string().uuid().allow(null),
        }),
    }),
    optoConnectPracticeId: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.valid(null),
        then: Koi.when('service.code', {
            is: ServiceCode.TOO,
            otherwise: Koi.valid(null),
            then: Koi.string().max(255).allow(null),
        }),
    }),
    receiveZorgmailOnConsultOpen: Koi.when('service.serviceType', {
        is: ServiceType.consultation,
        otherwise: Koi.valid(false),
        then: Koi.boolean(),
    }),
    recipientIsOwnOrganization: Koi.when('service.code', {
        is: ServiceCode.OSASV,
        otherwise: Koi.valid(false),
        then: Koi.boolean(),
    }),
    sendBittiumRecordingsTo: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.valid(null),
        then: Koi.when('service.code', {
            is: ServiceCode.TCOH,
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(BittiumRecordingAnalyst).allow(null),
        }),
    }),
    service: serviceSchema,
    serviceGraderPoolId: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.valid(null),
        then: Koi.string().uuid().allow(null),
    }),
    serviceGroupSettingId: Koi.when('service.serviceType', {
        is: ServiceType.examination,
        otherwise: Koi.string().uuid().allow(null),
        then: Koi.when('service.code', {
            is: ServiceCode.GGZOembl,
            otherwise: Koi.string().uuid().allow(null),
            then: Koi.string().uuid(),
        }),
    }),
    serviceId: Koi.string().uuid(),
    settingLabels: Koi.array()
        .items(Koi.string().valid(...settingNames))
        .optional(),
    somnologyOrganizationType: Koi.when('service.serviceType', {
        is: Koi.valid(ServiceType.examination, ServiceType.referralSmall),
        otherwise: Koi.valid(null),
        then: Koi.when('service.code', {
            is: Koi.valid(ServiceCode.OSASV, ServiceCode.OSASO, ServiceCode.OSASVO),
            otherwise: Koi.valid(null),
            then: Koi.koi().enum(SomnologyOrganizationType).allow(null),
        }),
    }),
    startDate: Koi.koi().date().required(),
    treatmentWaitingTime: Koi.when('service.serviceType', {
        is: ServiceType.referral,
        otherwise: Koi.valid(null),
        then: Koi.number().min(0).max(300).allow(null),
    }),
    zorgmailNummer: zorgMailNummerSchema.when('receiveZorgmailOnConsultOpen', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.disallow(null),
    }),
});

const preferredServiceOrganizationConsumersAdd = Koi.array().items(Koi.string().uuid()).min(1);

export const organizationAddCarePathServiceOrganizationSettingsSchema = serviceOrganizationSettingsSchema.keys({
    preferredServiceOrganizationConsumers: preferredServiceOrganizationConsumersAdd.when(
        Koi.ref('...serviceProviderSettings.isServiceConsumerSelector'),
        {
            is: true,
            otherwise: Koi.optional(),
            then: Koi.required(),
        }
    ),
});

// eslint-disable-next-line @stylistic/max-len
export const addCarePathServiceSettingsSchema: ISchemaContainer<IOrganizationAddCarePathServiceOrganizationSettingsValidated> =
    {
        schema: Koi.object()
            .keys({
                serviceOrganizationSettings: Koi.array().items(
                    organizationAddCarePathServiceOrganizationSettingsSchema
                ),
            })
            .options({ convert: false, presence: 'required' }),
    };

export interface IAddRightsStateValidated extends IAddRightsSettingsValidated {
    addServiceReader: boolean;
    employment: { id: string; organizationId: string; startDate: string };
    episodeCategory: EpisodeCategory;
    preferredServiceOrganizationConsumers: { organizationId: string; serviceId: string }[];
    serviceOrganizationSettings: IServiceOrganizationSettingsValidated[];
}

export interface IAddCarePathSettingsValidated {
    serviceConsumerSettings: IAddCarePathServiceConsumerSettingsValidated[];
    serviceProviderSettings: IAddCarePathServiceProviderSettingsValidated[];
    serviceSupervisorSettings: IAddCarePathServiceSupervisorSettingsValidated[];
}

export interface IAddRightsSettingsValidated {
    serviceConsumerSettings: IServiceConsumerSettingsValidated[];
    serviceEmploymentActivitiesSettings: IServiceEmploymentActivitiesSettingsValidated[];
    serviceProviderSettings: IServiceProviderSettingsValidated[];
    serviceSupervisorSettings: IServiceSupervisorSettingsValidated[];
}

export const addCarePathProfileSettingsSchema: ISchemaContainer<IAddCarePathSettingsValidated> = {
    schema: Koi.object()
        .keys({
            serviceConsumerSettings: Koi.array().items(addCarePathServiceConsumerSettingsAdd),
            serviceProviderSettings: Koi.array().items(addCarePathServiceProviderSettingsAdd),
            serviceSupervisorSettings: Koi.array().items(addCarePathServiceSupervisorSettingsAdd),
        })
        .options({ convert: false, presence: 'required' }),
};

const addRightsSettings = Koi.object().keys({
    serviceConsumerSettings: Koi.array().items(serviceConsumerSettingsAdd),
    serviceEmploymentActivitiesSettings: Koi.array().items(serviceEmploymentActivitiesSettingsAdd),
    serviceProviderSettings: Koi.array().items(serviceProviderSettingsAdd),
    serviceSupervisorSettings: Koi.array().items(serviceSupervisorSettingsAdd),
});

export const addRightsSettingsSchema: ISchemaContainer<IAddRightsSettingsValidated> = {
    schema: addRightsSettings.options({ convert: false, presence: 'required' }),
};

export const addRightsState = Koi.object()
    .keys({
        addServiceReader: Koi.boolean(),
        employment: Koi.object().keys({
            id: Koi.string(),
            organizationId: Koi.string(),
            startDate: Koi.string(),
        }),
        episodeCategory: Koi.koi().enum(EpisodeCategory),
        preferredServiceOrganizationConsumers: Koi.array().items(
            Koi.object().keys({
                organizationId: Koi.string(),
                serviceId: Koi.string(),
            })
        ),
        serviceOrganizationSettings: Koi.array().items(serviceOrganizationSettingsSchema),
    })
    .options({ convert: false, presence: 'required' });

export const addRightsSchema: ISchemaContainer<IAddRightsStateValidated> = {
    schema: addRightsSettings.concat(addRightsState),
};

export const addServiceActivitiesSchema: ISchemaContainer<IServiceEmploymentActivitiesSettingsValidated> = {
    schema: Koi.object().keys({
        serviceEmploymentActivitiesSettings: Koi.array().items(serviceEmploymentActivitiesSettingsAdd),
    }),
};

export interface IAddCarePathSaveableServiceValidated extends IServiceValidated {
    serviceConsumerSettings?: IAddCarePathServiceConsumerSettingsValidated;
    serviceEmploymentSettings?: IAddCarePathServiceEmploymentSettingsValidated;
    serviceProviderSettings?: IAddCarePathServiceProviderSettingsValidated;
    serviceSupervisorSettings?: IAddCarePathServiceSupervisorSettingsValidated;
}
export interface IOrganizationAddCarePathSaveableServiceValidated extends IAddCarePathSaveableServiceValidated {
    employments: { employmentId: string; episodeCategoryId: string; profileId: string }[];
    serviceOrganizationSettings: IOrganizationAddCarePathServiceOrganizationSettingsValidated;
}

export interface IPractitionerAddCarePathSaveableServiceValidated extends IAddCarePathSaveableServiceValidated {
    preferredServiceOrganizationConsumers?: string[];
    serviceOrganizationSettings?: IServiceOrganizationSettingsValidated;
}
export interface IOrganizationAddCarePathValidated {
    episodeCategoryId: string;
    organizationId: string;
    serviceReaderEmploymentIds?: string[];
    serviceReaderSettings?: IAddCarePathServiceReaderSettingsValidated;
    services: IOrganizationAddCarePathSaveableServiceValidated[];
}
export interface IPractitionerAddCarePathValidated {
    employmentId: string;
    episodeCategoryId: string;
    organizationId: string;
    serviceReaderSettings?: IAddCarePathServiceReaderSettingsValidated;
    services: IPractitionerAddCarePathSaveableServiceValidated[];
}

export const saveableOrganizationAddCarePathServiceAdd = serviceSchema.keys({
    employments: Koi.array().items(
        Koi.object().keys({
            employmentId: Koi.string().uuid(),
            episodeCategoryId: Koi.string().uuid(),
            profileId: Koi.string().uuid(),
        })
    ),
    serviceConsumerSettings: serviceConsumerSettingsAdd.optional(),
    serviceEmploymentSettings: serviceEmploymentSettingsAdd.optional(),
    serviceOrganizationSettings: organizationAddCarePathServiceOrganizationSettingsSchema.required(),
    serviceProviderSettings: serviceProviderSettingsAdd.optional(),
    serviceSupervisorSettings: serviceSupervisorSettingsAdd.optional(),
});

export const saveablePractitionerAddCarePathServiceAdd = serviceSchema.keys({
    preferredServiceOrganizationConsumers: preferredServiceOrganizationConsumersAdd.when(
        Koi.ref('serviceProviderSettings.isServiceConsumerSelector'),
        {
            is: true,
            otherwise: Koi.optional(),
            then: Koi.required(),
        }
    ),
    serviceConsumerSettings: serviceConsumerSettingsAdd.optional(),
    serviceEmploymentSettings: serviceEmploymentSettingsAdd.optional(),
    serviceOrganizationSettings: serviceOrganizationSettingsSchema.optional(),
    serviceProviderSettings: serviceProviderSettingsAdd.optional(),
    serviceSupervisorSettings: serviceSupervisorSettingsAdd.optional(),
});

export const organizationCarePathAddSchema: ISchemaContainer<IOrganizationAddCarePathValidated> = {
    schema: Koi.object().keys({
        episodeCategoryId: Koi.string().uuid().required(),
        organizationId: Koi.string().uuid().required(),
        serviceReaderEmploymentIds: Koi.array().items(Koi.string().uuid()).optional(),
        serviceReaderSettings: serviceReaderSettingsAdd.optional(),
        services: Koi.array().items(saveableOrganizationAddCarePathServiceAdd),
    }),
};

export const practitionerCarePathAddSchema: ISchemaContainer<IPractitionerAddCarePathValidated> = {
    schema: Koi.object().keys({
        employmentId: Koi.string().uuid().required(),
        episodeCategoryId: Koi.string().uuid().required(),
        organizationId: Koi.string().uuid().required(),
        serviceReaderSettings: serviceReaderSettingsAdd.optional(),
        services: Koi.array().items(saveablePractitionerAddCarePathServiceAdd),
    }),
};
